import React, { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { DisplaySpinnerContext, RouteGuardContext, TabPanelContext } from "../../../../context/ContextIndex";
import { selectSessionConfig } from "../../../../store/store-selector";
import { AppForm, DynamicFormContainer } from "../../../../components";
import { getDeviceDefaultValue, getDeviceSchema } from "./schema";
import { getReplacementDeviceDefaultValue } from "../../replacement-device/create/schema";
import { useFetchListData } from "../../../../hooks/useFetchListData";
import { getReplacementDevice } from "../../api-handler/api-handler";
import _ from "lodash";

const DeviceCreate = forwardRef(({ setDeviceFormData, setIsFormValid }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const { formState, clearErrors, watch } = useFormContext();
  const _tabContextObject = useContext(TabPanelContext);
  const _tabContext = _tabContextObject.getTabFormContext();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const routeGuardContext = useContext(RouteGuardContext);

  const EditDeviceId = searchParams.get("deviceid");
  const navigationData = location.state;
  const settingsConfig = useSelector(selectSessionConfig);

  const DeviceTypeList = useFetchListData(settingsConfig.businessCode, settingsConfig.countryCode, "getDeviceType", "devicetype", { required: true, clause: "&status=active" });
  const ServiceTypeList = useFetchListData(settingsConfig.businessCode, settingsConfig.countryCode, "getServiceType", "servicetype", { required: true, clause: "&status=active" });
  useImperativeHandle(ref, () => ({
    tabChangeHandler,
  }));

  //Event Handlers
  const handleCancel = () => {
    try {
      navigate("/devicelist");
    } catch (err) {
      alert(err);
    }
  };

  const tabChangeHandler = (newtab) => {
    if (formState.isValid) {
      onDeviceSubmit(newtab);
    }
  };

  const onDeviceSubmit = (newtab = "1") => {
    try {
      const _formStateData = watch();
      clearErrors();
      _tabContextObject.setTabFormContext({
        tabIndex: newtab,
        data: {
          ..._tabContext.data,
          device: _formStateData,
        },
        isDirty: EditDeviceId ? !_.isEqual(_tabContext.prevData?.device, _formStateData) : true,
      });
    } catch (e) {
      alert(e);
    }
  };

  //Use Effects
  useEffect(() => {
    const setFormSchema = () => {
      try {
        let _lookUpData = {};
        _lookUpData.deviceTypeList = [];
        _lookUpData.serviceTypeList = [];

        let _formData = navigationData ? { ...navigationData } : null;
        console.log("Inside setFormSchema _formData ::::", _formData);
        if (DeviceTypeList.length) {
          _lookUpData.deviceTypeList = DeviceTypeList.map((item) => {
            if (_formData && item.DeviceTypeId === _formData.DeviceTypeId) {
              _formData.DeviceType = {
                id: item.DeviceTypeId,
                label: item.TypeName,
              };
            }
            return {
              id: item.DeviceTypeId,
              label: item.TypeName,
            };
          });
        }

        if (ServiceTypeList.length) {
          _lookUpData.serviceTypeList = ServiceTypeList.map((item) => {
            if (_formData && item.ServiceTypeId === _formData.TypeId) {
              _formData.Type = {
                id: item.ServiceTypeId,
                label: item.ServiceName,
              };
            }
            return {
              id: item.ServiceTypeId,
              label: item.ServiceName,
            };
          });
        }

        if (_formData && EditDeviceId) {
          _tabContextObject.setTabFormContext({
            editDeviceId: EditDeviceId,
            prevData: {
              device: getDeviceDefaultValue(_formData),
            },
            isDirty: false,
          });

          setDeviceFormData((prevstate) => ({
            ...prevstate,
            deviceSchema: getDeviceSchema(_lookUpData),
            appformdata: getDeviceDefaultValue(
              _tabContext.data?.device ? _tabContext.data?.device : _formData
            ),
          }));
        } else {
          setDeviceFormData((prevstate) => ({
            ...prevstate,
            deviceSchema: getDeviceSchema(_lookUpData),
          }));
        }
      } catch (err) {
        alert(err);
      }
    };

    const fetchReplacementDevices = async () => {
      try {
        if (EditDeviceId && !_tabContext?.data?.replacementDevice) {
          // Make the API call here to get the replacement device
          let response = await getReplacementDevice(EditDeviceId, settingsConfig);

          // Store the fetched data in the context
          _tabContextObject.setTabFormContext({
            data: {
              replacementDevice: response.map((item, index) => ({
                id: index,
                ...getReplacementDeviceDefaultValue(item)
              })),
            },
            prevData: {
              replacementDevice: response,
            },
          });
        }
      } catch (err) {
        alert("Failed to fetch replacement device.");
      } finally {
        spinnerCtx.setLoadingIndicator(false);
      }
    };

    spinnerCtx.setLoadingIndicator(true);
    if (DeviceTypeList && ServiceTypeList) {
      setFormSchema();
      fetchReplacementDevices();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DeviceTypeList, ServiceTypeList, EditDeviceId, navigationData]);

  useEffect(() => {
    routeGuardContext.setFormState(_tabContext?.isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppForm onSubmit={onDeviceSubmit} setFormValid={setIsFormValid}>
      <DynamicFormContainer
        header={t("createDevice.device")}
        onCancel={handleCancel}
        onSaveLabel={t("generic.next")}
        isWithAppForm={false}
        onSubmit={onDeviceSubmit}
      />
    </AppForm>
  );
});

export default DeviceCreate;
